import { getFuzzyMatchPosition } from '@direct-frontend/stdlib';
import { ReactNode, useContext, useMemo } from 'react';

import { TextHighlighter } from '../../TextHighlighter';
import { ListSearchContext } from '../utils';

interface ListItemSearchState {
    /** Поиск включён и список фильтруется по запросу */
    isSearchActive: boolean;
    /** Элемент подходит под поисковый запрос */
    isSearchMatch: boolean;
    /** Текст совпадения с подсветкой. Если поиск выключен — `null` */
    highlightedChildren: ReactNode;
}

/**
 * Возвращает текст, по которому ищем элемент, или `null`, если элемент в поиске не участвует
 */
function getSearchableText(children: ReactNode, textValue?: string): string | null {
    if (textValue !== undefined) {
        return textValue;
    }

    if (typeof children === 'string') {
        return children;
    }

    return null;
}

/**
 * Определяет, попадает ли элемент в поисковый запрос, и готовит содержимое с подсветкой совпадения
 */
export function useListItemSearch(children: ReactNode, textValue?: string | null): ListItemSearchState {
    const { isActive, query } = useContext(ListSearchContext);

    return useMemo(() => {
        if (!isActive) {
            return {
                isSearchActive: false,
                isSearchMatch: false,
                highlightedChildren: null,
            };
        }

        if (textValue === null) {
            // При явной передаче `null` элемент исключается из поиска
            return {
                isSearchActive: true,
                isSearchMatch: false,
                highlightedChildren: null,
            };
        }

        const searchableText = getSearchableText(children, textValue);

        if (searchableText === null) {
            return {
                isSearchActive: true,
                isSearchMatch: false,
                highlightedChildren: null,
            };
        }

        const matchRange = getFuzzyMatchPosition(searchableText, query);

        if (!matchRange) {
            return {
                isSearchActive: true,
                isSearchMatch: false,
                highlightedChildren: null,
            };
        }

        return {
            isSearchActive: true,
            isSearchMatch: true,
            highlightedChildren: (
                <>
                    {searchableText.slice(0, matchRange.from)}
                    <TextHighlighter type="selection" data-testid="ListItem.Highlight">
                        {searchableText.slice(matchRange.from, matchRange.to)}
                    </TextHighlighter>
                    {searchableText.slice(matchRange.to)}
                </>
            ),
        };
    }, [children, isActive, query, textValue]);
}
